package gr.tech.site.pages.states

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.components.states.UtilityCardData
import gr.tech.site.components.states.UtilityPartnerGrid
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.states.MinnesotaHeroTextStyle
import gr.tech.site.styles.states.StateStatIconStyle
import gr.tech.site.styles.states.StateStatLabelStyle
import gr.tech.site.styles.states.StateStatNumberStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initMinnesotaPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Minnesota - HVAC Rebates & LED Horticulture Incentives",
        description = "Xcel Energy offers up to 100% back on LED grow lights, HVAC, and dehumidification for commercial horticulture and cannabis growers in Minnesota. Green Rebates handles the paperwork.",
        canonicalUrl = "https://greenrebates.com/states/minnesota",
        ogImage = "/images/minnesota1.jpg"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun MinnesotaPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        )
        {
            // Hero Image
            Img(
                src = ImageRepo.Minnesota1.src,
                alt = ImageRepo.Minnesota1.alt,
                attrs = Modifier.fillMaxSize().toAttrs{
                    attr("fetchpriority", "high") // ensures LCP optimization
                    style {
                        objectFit(ObjectFit.Cover) // preserve background-like cover
                    }
                }
            )

            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Black)
                    .opacity(30.percent)
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .toAttrs()
            )

            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            )
            {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .then(MinnesotaHeroTextStyle.toModifier())
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) {
                    Text("Minnesota Horticulture Energy Rebates")
                }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .then(MinnesotaHeroTextStyle.toModifier())
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Minnesota Growers: Capture Your LED Grow Light & HVAC Rebates")
                }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .then(MinnesotaHeroTextStyle.toModifier())
                        .classNames("mb-4", "fw-medium", "text-white").toAttrs()
                ) {
                    Text("Green Rebates works directly with Xcel Energy to help Minnesota growers claim up to 100% funding on DLC-qualified LED horticulture lighting, commercial HVAC, and dehumidification upgrades.")
                }

                Link(
                    path = "/contact-us?state=Minnesota",
                    text = "Free Rebates Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free Minnesota grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) {
                    Text("How to Claim Horticulture Rebates in Minnesota")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Three steps to unlock LED grow light rebates, HVAC incentives, and more from Minnesota utility programs.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        Triple("1", "Tell Us About Your Grow Operation",
                            "Share your facility details and planned equipment upgrades so we can identify every available LED horticulture and HVAC rebate for your Minnesota operation."),
                        Triple("2", "We Handle the Utility Applications",
                            "Minnesota utility rebate programs require precise documentation and pre-approval. We manage every submission, communication, and follow-up with Xcel Energy on your behalf."),
                        Triple("3", "You Collect Your Incentive Check",
                            "Sit back while we secure your LED grow light rebates, HVAC incentives, and any other eligible programs — maximizing every dollar returned to your facility.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier()
                                        .classNames("mb-1")
                                        .toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier()
                                        .classNames("mb-0")
                                        .toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Link(
                        path = "/contact-us?state=Minnesota",
                        text = "Contact Us Now \u2192",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaBlackOutlineStyle.toModifier().classNames("mx-auto")
                            .role("button")
                            .attrsModifier { attr("aria-label", "Contact Us") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
            }
        }

        // TYPES OF REBATES AVAILABLE
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("Types of Rebates Available in Minnesota")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Xcel Energy offers a range of energy efficiency incentives for commercial cultivation, indoor grow, and greenhouse operations across Minnesota.")
                }

                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4")
                        .toAttrs()
                ) {
                    listOf(
                        "LED Grow Light Rebates" to
                            "Switching to DLC-qualified LED horticulture lighting qualifies for strong Xcel Energy rebates. Minnesota cultivation facilities can recover a significant portion of their LED retrofit costs.",
                        "Commercial HVAC Incentives" to
                            "Precise climate control is critical for indoor grow operations. Minnesota's commercial HVAC rebate programs help offset equipment and installation costs for cultivation facilities.",
                        "Dehumidification Rebates" to
                            "High-humidity grow environments are prime candidates for dehumidification incentives. Get paid to reduce energy consumption and operating costs in your Minnesota facility.",
                        "Custom Grow & Greenhouse Programs" to
                            "Xcel Energy offers custom incentive programs for large indoor grows and commercial greenhouse operations that require tailored energy efficiency solutions."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Why Minnesota Growers Choose Green Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        "Minnesota Utility Expertise" to
                                "We know Xcel Energy's rebate programs inside and out — including every LED horticulture, HVAC, and indoor grow incentive available to Minnesota facilities.",
                        "Full-Service for Grow Operations" to
                                "From grow room audits to rebate submissions and checks, we manage every step of the process for cannabis cultivators, mezzobusinesses, microbusinesses, greenhouse operators, and other licensed Minnesota grow operations.",
                        "DLC-Qualified Product Guidance" to
                                "We help you select DLC-qualified LED grow lights and high-efficiency HVAC equipment that maximize your Minnesota rebate eligibility from the start.",
                        "Stacked Incentive Strategy" to
                                "We identify every opportunity to stack Minnesota utility rebates with other available energy incentive programs — ensuring your facility captures the maximum possible funding.",
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }

                P(attrs = LandingMutedWhiteBodyStyle.toModifier().classNames("text-center").toAttrs()) {
                    Text("All of this is powered by our close, trusted partnership with Xcel Energy in Minnesota.")
                }
            }
        }

        // TRUSTED UTILITIES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "pt-5", "pb-3")
                .id("utilities")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "pb-1").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("Minnesota Utility Rebate Programs")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Green Rebates works directly with Xcel Energy to secure LED grow light rebates, commercial HVAC incentives, and horticulture energy savings programs for Minnesota growers. " +
                            "We handle the details so your indoor grow or greenhouse captures every available rebate.")
                }

                UtilityPartnerGrid(listOf(
                    UtilityCardData(utilityName = "Xcel Energy", state = "Minnesota", utilityParam = "xcel-mn")
                ))

            }
        }

        // STATS + TESTIMONIALS SECTION
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            H2(
                attrs = LandingSectionHeadingStyle.toModifier()
                    .classNames("mb-2")
                    .toAttrs()
            ) {
                Text("See How We Have Impacted ")
                SpanText(
                    text = "Minnesota",
                    modifier = Modifier.classNames("fw-italic")
                        .color(Theme.MediumGreen.rgb)
                )
            }

            P(
                attrs = LandingSectionSubtitleStyle.toModifier()
                    .classNames("mb-4")
                    .toAttrs()
            ) {
                Text("From shore to shore, growers across the Land of 10,000 Lakes are reeling in rebates.")
            }

            Div(attrs = Modifier.classNames("container", "mb-5").toAttrs()) {
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "g-lg-5", "justify-content-center")
                        .toAttrs()
                ) {
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaDollarSign(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("$55M+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Total Rebates Secured") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaClipboardList(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("400+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Projects Completed") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaBolt(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("420M") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("kWh Saved") }
                        }
                    }
                }
            }


            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "row-cols-1", "justify-content-center", "g-4").toAttrs()) {
                    Div(attrs = Modifier.classNames("col").toAttrs()) {
                        Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                            Span(
                                attrs = LandingQuoteMarkStyle.toModifier()
                                    .classNames("d-block", "mb-1")
                                    .toAttrs()
                            ) { Text("\u201C") }
                            P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                Text("So I started working with Green Rebates in 2022. Dylan Collins, the owner here, came up to me at a trade show. They gave me some huge number, and I was like 'there's no way this is gonna work.' It was 7 figures. I took the leap of faith with Green Rebates, felt comfortable to submit, then the first check came back to me and I was on cloud nine. I basically got a brand new facility with lights, dehums, etc. The check was 380 grand. Another one we did over a half a million. The joy you feel when you get a big check, I don't care what you do in your life. You get a couple hundred thousand dollar check, you're a happy guy.")
                            }
                            Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                Text("- Johnny (Minnesota Farmer)")
                            }
                        }
                    }
                }
            }
        }

        // FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("FAQs About Energy Rebates in Minnesota")
                }

                FaqAccordion(
                    accordionId = "minnesota-faq",
                    items = listOf(
                        FaqItem(
                            question = "Who qualifies for Minnesota energy rebates?",
                            answer = "Commercial growers, licensed cannabis and marijuana cultivators, greenhouse operators, and indoor horticulture facilities in Minnesota that pay utility bills to Xcel Energy may qualify. Most operations with significant LED lighting or HVAC loads are eligible."
                        ),
                        FaqItem(
                            question = "Does Xcel Energy offer rebates for LED grow lighting?",
                            answer = "Yes. Xcel Energy offers rebates for DLC-qualified LED grow light replacements in commercial cultivation and horticulture facilities across Minnesota. The rebate amount depends on the wattage and number of fixtures replaced."
                        ),
                        FaqItem(
                            question = "Is cannabis cultivation in Minnesota eligible for energy rebates?",
                            answer = "Yes. Since Minnesota legalized recreational cannabis cultivation in 2023, licensed cannabis grow facilities qualify for Xcel Energy's commercial rebate programs just like any other horticulture operation. LED grow lights, HVAC systems, and dehumidification upgrades are all eligible. This applies across all license tiers — cannabis cultivators (up to 30,000 sq ft of indoor canopy), mezzobusinesses (up to 15,000 sq ft), and microbusinesses (up to 5,000 sq ft) all operate significant lighting and climate control infrastructure that qualifies for rebates."
                        ),
                        FaqItem(
                            question = "What is Minnesota's Conservation Improvement Program (CIP)?",
                            answer = "Minnesota's Conservation Improvement Program (CIP) requires utilities like Xcel Energy to invest a percentage of their revenue in energy efficiency programs each year. This state mandate means Minnesota growers have access to consistently funded rebate programs for LED horticulture lighting, HVAC, and other upgrades — making it one of the stronger incentive markets in the Midwest."
                        )
                    )
                )
            }
        }

        RelatedSection(listOf(
            "/blog/lucky-leaf-2026" to "Lucky Leaf 2026 Recap | We're Lucky To Be Here",
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Ready to Claim Your Minnesota Grow Rebates?")
                }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("Don't let your Minnesota cultivation facility miss out on LED grow light rebates, HVAC incentives, and horticulture energy savings programs. Green Rebates handles everything — from application to check.")
                }
                Link(
                    path = "/contact-us?state=Minnesota",
                    text = "Get a Free Minnesota Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free Minnesota LED grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }


    }
}